import { useCallback, useEffect, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { CheckSquare, Clock, ExternalLink } from "lucide-react";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useEvent } from "../react/hooks/useEvent";
import { useWorkspace } from "../react/providers/WorkspaceProvider";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";
import { toast } from "../react/hooks/use-toast";
import { buildWorkspacePath, formatDueAt, slaBucket } from "../core";
import type {
    ActionDef,
    ApprovalCaseDetail,
    ApprovalTask,
    Json,
    MenuEntry,
} from "../core";

const NOTIFICATION_DEBOUNCE_MS = 500;

// Header bell for pending approvals. ALWAYS VISIBLE — even at zero pending —
// so the user knows where the inbox lives. Mirrors the original frontend's
// ApprovalNotification.tsx; the only intentional behavior change is dropping
// the `if (pendingApprovalCount <= 0) return null` gate.
export function ApprovalNotification() {
    const runtime = useRuntime();
    const boot = useWorkspace();
    const { orgCode, navigateTo } = useWorkspaceNavigation();

    const [open, setOpen] = useState(false);
    const [tasks, setTasks] = useState<ApprovalTask[]>([]);
    const [caseIndex, setCaseIndex] = useState<Record<string, ApprovalCaseDetail>>({});
    const [loading, setLoading] = useState(false);
    const [decidingId, setDecidingId] = useState<string | null>(null);
    const [pendingCount, setPendingCount] = useState(boot.pendingApprovalCount);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadInbox = useCallback(async () => {
        setLoading(true);
        try {
            const list = await runtime.services.approval.getInbox();
            setTasks(list);
            const uniqueCaseIds = Array.from(new Set(list.map((t) => t.case_id).filter(Boolean)));
            const detailEntries = await Promise.all(
                uniqueCaseIds.map(async (cid) => {
                    try {
                        return [cid, await runtime.services.approval.getCase(cid)] as const;
                    } catch {
                        return [cid, null] as const;
                    }
                }),
            );
            const idx: Record<string, ApprovalCaseDetail> = {};
            for (const [cid, detail] of detailEntries) {
                if (detail) idx[cid] = detail;
            }
            setCaseIndex(idx);
            setPendingCount(list.length);
        } catch {
            // Silent — popover stays empty if hydration fails.
        } finally {
            setLoading(false);
        }
    }, [runtime]);

    // Hydrate popover contents on each open.
    useEffect(() => {
        if (open) void loadInbox();
    }, [open, loadInbox]);

    // Live refresh of the badge + popover when an approval-domain push event arrives.
    useEvent("web.client.notification", (payload: Json) => {
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) return;
        const key = typeof (payload as Record<string, unknown>).event_key === "string"
            ? (payload as Record<string, unknown>).event_key as string
            : "";
        if (!key.startsWith("approval.")) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            void loadInbox();
            debounceRef.current = null;
        }, NOTIFICATION_DEBOUNCE_MS);
    });

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    async function handleQuickApprove(task: ApprovalTask, ev: React.MouseEvent) {
        ev.stopPropagation();
        if (decidingId) return;
        setDecidingId(task.record_uuid);
        try {
            await runtime.services.approval.decide(task.case_id, "APPROVE", "");
            toast({ title: "Approved." });
            setTasks((prev) => prev.filter((t) => t.record_uuid !== task.record_uuid));
            setPendingCount((c) => Math.max(0, c - 1));
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Failed to approve.",
                description: err instanceof Error ? err.message : String(err),
            });
        } finally {
            setDecidingId(null);
        }
    }

    function handleOpenInbox() {
        setOpen(false);
        if (!orgCode) return;
        const actionKey = findActionByClientComponent(boot.menus, "approval.inbox");
        if (!actionKey) return;
        navigateTo(buildWorkspacePath(orgCode, [{ kind: "action", actionKey, viewMode: "list" }]));
    }

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    className="icon-btn"
                    type="button"
                    title={`${pendingCount} pending approval${pendingCount === 1 ? "" : "s"}`}
                    aria-label="Pending approvals"
                >
                    <CheckSquare size={16} />
                    {pendingCount > 0 ? (
                        <span className="icon-btn__badge">{pendingCount > 9 ? "9+" : pendingCount}</span>
                    ) : null}
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="notif-pane notif-pane--approval" align="end" sideOffset={6}>
                    <div className="notif-pane__header">
                        <span className="notif-pane__title">Pending Approvals</span>
                        <button type="button" className="notif-pane__link" onClick={handleOpenInbox}>
                            Open inbox
                        </button>
                    </div>

                    {loading ? (
                        <div className="notif-pane__empty">
                            <span>Loading…</span>
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="notif-pane__empty">
                            <CheckSquare size={28} className="notif-pane__empty-icon" />
                            <span>No pending approvals</span>
                        </div>
                    ) : (
                        <div className="notif-pane__list">
                            {tasks.map((task) => {
                                const detail = caseIndex[task.case_id];
                                const subject = detail?.case.subject ?? "(loading…)";
                                const domain = detail?.case.domain ?? "";
                                const bucket = slaBucket(task.due_at);
                                const isDeciding = decidingId === task.record_uuid;
                                return (
                                    <div key={task.record_uuid} className="notif-item notif-item--approval">
                                        <span className="notif-item__icon notif-item__icon--info">
                                            <CheckSquare size={14} />
                                        </span>
                                        <span className="notif-item__body">
                                            <span className="notif-item__subject">{subject}</span>
                                            {domain ? (
                                                <span className="notif-item__domain">{domain}</span>
                                            ) : null}
                                            <span className={`notif-item__due notif-item__due--${bucket}`}>
                                                <Clock size={10} />
                                                {formatDueAt(task.due_at)}
                                            </span>
                                        </span>
                                        <span className="notif-item__approval-actions">
                                            <button
                                                type="button"
                                                disabled={isDeciding}
                                                className="notif-item__approve-btn"
                                                onClick={(ev) => void handleQuickApprove(task, ev)}
                                            >
                                                {isDeciding ? "…" : "Approve"}
                                            </button>
                                            <button
                                                type="button"
                                                className="notif-item__open-btn"
                                                onClick={handleOpenInbox}
                                                title="Open in inbox to reject or comment"
                                                aria-label="Open in inbox"
                                            >
                                                <ExternalLink size={12} />
                                            </button>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// ── helpers ──────────────────────────────────────────────────────────────

function findActionByClientComponent(menusByApp: Record<string, MenuEntry[]>, component: string): string | null {
    for (const menus of Object.values(menusByApp)) {
        const found = walkMenus(menus, component);
        if (found) return found;
    }
    return null;
}

function walkMenus(menus: MenuEntry[], component: string): string | null {
    for (const m of menus) {
        const ac = m.action as ActionDef | undefined;
        if (ac?.clientComponent === component && ac.key) return ac.key;
        if (m.children) {
            const found = walkMenus(m.children, component);
            if (found) return found;
        }
    }
    return null;
}
